// The Overlays section of the panel: a checkbox per scheme with its color,
// a remove button on user schemes, and the add dialog.

import { schemeColor, SchemeError, validateScheme, newUserSchemeId, type Scheme } from '../overlays/scheme.ts'

export interface SchemePanelElements {
  list: HTMLElement
  addButton: HTMLButtonElement
  dialog: HTMLDialogElement
  json: HTMLTextAreaElement
  error: HTMLElement
}

export interface SchemePanelHandlers {
  onVisibleChange: (ids: string[]) => void
  onAdd: (scheme: Scheme) => void
  onRemove: (id: string) => void
}

export class SchemePanel {
  readonly #el: SchemePanelElements
  readonly #on: SchemePanelHandlers
  #schemes: Scheme[] = []

  constructor(elements: SchemePanelElements, handlers: SchemePanelHandlers) {
    this.#el = elements
    this.#on = handlers

    elements.addButton.addEventListener('click', () => {
      elements.error.textContent = ''
      elements.dialog.showModal()
      elements.json.focus()
    })

    const form = elements.dialog.querySelector('form')!
    form.addEventListener('submit', (event) => {
      const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null
      if (submitter?.value !== 'add') return // Cancel closes via method=dialog
      event.preventDefault()
      let parsed: unknown
      try {
        parsed = JSON.parse(elements.json.value)
      } catch (error) {
        elements.error.textContent = `Not valid JSON: ${(error as Error).message}`
        return
      }
      try {
        const scheme = validateScheme(parsed, newUserSchemeId())
        this.#on.onAdd(scheme)
        elements.json.value = ''
        elements.dialog.close('add')
      } catch (error) {
        elements.error.textContent = error instanceof SchemeError ? error.message : String(error)
      }
    })
  }

  /** Ids of the checked schemes, in panel order. */
  visibleIds(): string[] {
    return [...this.#el.list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')]
      .filter((box) => box.checked)
      .map((box) => box.dataset.id!)
  }

  /** Color for a scheme by its position in the full list, so it never shifts. */
  colorOf(id: string): string {
    const index = this.#schemes.findIndex((s) => s.id === id)
    return schemeColor(Math.max(0, index))
  }

  render(schemes: readonly Scheme[], visibleIds: readonly string[]): void {
    this.#schemes = [...schemes]
    const visible = new Set(visibleIds)
    const list = this.#el.list
    list.replaceChildren()

    schemes.forEach((scheme, index) => {
      const row = document.createElement('label')
      row.className = 'scheme-row'

      const box = document.createElement('input')
      box.type = 'checkbox'
      box.checked = visible.has(scheme.id)
      box.dataset.id = scheme.id
      // Read every box at event time rather than a snapshot from render time,
      // so the answer is always what the panel currently shows.
      box.addEventListener('change', () => this.#on.onVisibleChange(this.visibleIds()))

      const swatch = document.createElement('span')
      swatch.className = 'scheme-swatch'
      swatch.style.background = schemeColor(index)

      const name = document.createElement('span')
      name.className = 'scheme-name'
      name.textContent = scheme.name
      name.title = scheme.source ?? scheme.name

      row.append(box, swatch, name)

      if (!scheme.builtin) {
        const remove = document.createElement('button')
        remove.type = 'button'
        remove.className = 'scheme-remove'
        remove.textContent = '×'
        remove.title = 'Remove this scheme'
        remove.addEventListener('click', (event) => {
          event.preventDefault()
          this.#on.onRemove(scheme.id)
        })
        row.append(remove)
      }
      list.append(row)
    })
  }
}
